import FeatureHandler from "./handlers/FeatureHandler";
import MetadataHandler from "./handlers/MetadataHandler";
import HeaderHandler from "./handlers/HeaderHandler";
import SkinSettingsHandler from "./handlers/SkinSettingsHandler";
import PreviewHandler from "./handlers/PreviewHandler";
import PlaceholderHandler from "./handlers/PlaceholderHandler";

export default class dataProvider {
    constructor(id, queryParams) {
        this.id = id;
        this.queryParams = queryParams;
        this.vue = window.AppKit.getApp();

        this.getBreakpoints = this.getBreakpoints.bind(this);
        this.getBlocks = this.getBlocks.bind(this);
        this.getBlockGroupings = this.getBlockGroupings.bind(this);
        this.getBlockTypes = this.getBlockTypes.bind(this);
        this.getVisibility = this.getVisibility.bind(this);
        this.getOriginalBreakpoint = this.getOriginalBreakpoint.bind(this);
        this.fetchBlueprintData = this.fetchBlueprintData.bind(this);
        this.getMetadataHandler = this.getMetadataHandler.bind(this);
        this.getHeaderHandler = this.getHeaderHandler.bind(this);
        this.getSkinSettingsHandler = this.getSkinSettingsHandler.bind(this);
        this.getPreviewHandler = this.getPreviewHandler.bind(this);
        this.getPlaceholderHandler = this.getPlaceholderHandler.bind(this);
        this.getTitle = this.getTitle.bind(this);
        this.getListUrl = this.getListUrl.bind(this);
        this.getListLabel = this.getListLabel.bind(this);
        this.fetchInterfaceData = this.fetchInterfaceData.bind(this);
        this.getEventListeners = this.getEventListeners.bind(this);
        this.getTopBarWidgets = this.getTopBarWidgets.bind(this);
        this.getWidgetBaseUrl = this.getWidgetBaseUrl.bind(this);
        this.getPlaceholderUrl = this.getPlaceholderUrl.bind(this);
        this.getPreviewUrl = this.getPreviewUrl.bind(this);
        this.getVersionsActive = this.getVersionsActive.bind(this);
        this.getVersionsCount = this.getVersionsCount.bind(this);
    }

    /**
     * Get block data
     * @returns {[{blocks: [{blocks: [], id: number, title: string, type: string}, {blocks: [], id: number, title: string, type: string}, {blocks: [], id: number, title: string, type: string}, {blocks: [], id: number, title: string, type: string}], id: number, title: string, type: string}, {blocks: [{blocks: [], id: number, title: string, type: string}], id: number, title: string, type: string}, {blocks: [{blocks: [], id: number, title: string, type: string}], id: number, title: string, type: string}]}
     */
    getBlocks() {
        if(! this.blocks) {
            this.fetchBlueprintData()
        }

        return this.blocks ? this.blocks : [];
    }

    /**
     * Get block data
     * @returns {[{blocks: [{blocks: [], id: number, title: string, type: string}, {blocks: [], id: number, title: string, type: string}, {blocks: [], id: number, title: string, type: string}, {blocks: [], id: number, title: string, type: string}], id: number, title: string, type: string}, {blocks: [{blocks: [], id: number, title: string, type: string}], id: number, title: string, type: string}, {blocks: [{blocks: [], id: number, title: string, type: string}], id: number, title: string, type: string}]}
     */
    getGrid() {
        if(! this.grid) {
            this.fetchBlueprintData()
        }

        return this.grid ? this.grid : [];
    }

    /**
     * Get title
     * @returns {*|null}
     */
    getTitle() {
        if(! this.title) {
            this.fetchBlueprintData()
        }

        return this.title ? this.title : null;
    }

    /**
     * Get visibility
     * @returns {*|{publishedStart: *, publishedEnd: *, published}}
     */
    getVisibility() {
        if(! this.visibility) {
            this.fetchBlueprintData()
        }

        return this.visibility;
    }

    /**
     * Get the possible block types
     * @returns {[{minColspan: number, allowChildren: boolean, icon: string, title: string, type: string}, {minColspan: number, allowChildren: boolean, icon: string, title: string, type: string}, {minColspan: number, allowChildren: boolean, icon: string, title: string, type: string}, {minColspan: number, allowChildren: boolean, icon: string, title: string, type: string}]}
     */
    async getBlockTypes() {
        if(! this.supportedBlocks) {
            this.fetchInterfaceData();
        }

        return this.supportedBlocks;
    }

    /**
     * Get block groupings
     * @return {Promise<*>}
     */
    async getBlockGroupings() {
        if(! this.blockGroupings) {
            this.fetchInterfaceData();
        }

        return this.blockGroupings;
    }

    /**
     * Get the list url
     * @returns {*}
     */
    async getListUrl() {
        if(!  this.listUrl) {
            await this.fetchInterfaceData();
        }

        return this.listUrl;
    }

    /**
     * Get the list label
     * @returns {*}
     */
    getListLabel() {
        return this.listLabel;
    }

    /**
     * Get the list url
     * 
     * @param forceRefresh
     * @return {Promise<*>}
     */
    async getTopBarWidgets(forceRefresh = false) {
        if(!  this.topBarWidgets || forceRefresh) {
            await this.fetchInterfaceData();
        }

        return this.topBarWidgets;
    }

    /**
     * Get version active
     * @return {*}
     */
    getVersionsActive() {
        return this.versionsActive;
    }

    /**
     * Get versions count
     *
     * @returns {*}
     */
    getVersionsCount() {
        return this.versionsCount;
    }

    /**
     * Get all breakpoint data
     * @returns {[{isOriginal: boolean, blockPositioning, title: string}, {isOriginal: boolean, blockPositioning, title: string}, {isOriginal: boolean, blockPositioning, title: string}]}
     */
    getBreakpoints() {
        if(! this.breakpoints) {
            this.fetchBlueprintData()
        }

        return this.breakpoints ? this.breakpoints : [];
    }

    /**
     * Get the original breakpoint
     * @returns {{isOriginal: boolean, blockPositioning, title: string}}
     */
    getOriginalBreakpoint() {
        return this.getBreakpoints().find( breakpoint => breakpoint.isOriginal === true);
    }

    /**
     * Generate a new unique referenceID
     */
    getNewReferenceId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    /**
     * We construct the data for a new block here
     * @param type
     * @returns {{colspan: number, minColspan, offset: number, blocks: *[], areChildrenAllowed, id: *, title: string, titleDefault, requiredParentType: (string|*)}}
     */
    getNewBlockByType(type) {
        return {
            id: null,
            referenceId: this.getNewReferenceId(),
            name: type.name,
            nameDefault: type.nameDefault,
            canEditName: type.canEditName,
            type: type.type,
            minColspan: type.minColspan,
            colspan: 12,
            offset: 0,
            endOfRow: false,
            areChildrenAllowed: type.areChildrenAllowed,
            requiredParent: type.requiredParent,
            settings: type.settings,
            locks: type.locks,
            conditions: type.conditions,
            icon: type.icon,
            size: type.size,
            helpText: type.helpText,
            blocks: []
        }
    }

    /**
     * This is the default blockpositioning for a new block
     * @param id
     * @returns {{blockId, colspan: number, orderIndexOriginal: number, fullWidth: boolean, offset: number, orderIndexDiff: number, orderIndex: number, endOfRow: boolean, hidden: boolean}}
     */
    getBlockPositioningForNewBlock(id) {
        return {
            referenceId: id,
            blockId: null,
            orderIndex: null,
            colspan: 12,
            offset: 0,
            hidden: false,
            fullWidth: false,
            alignment: null,
        }
    }

    /**
     * Get the features list
     * @returns {[{name: string, id: string, messageUnsupported: string, supported: boolean},{name: string, id: string, messageUnsupported: string, supported: boolean}]}
     */
    getFeatures() {
        // return [
        //     {
        //         id: 'breakpoints-specific-block-sorting',
        //         name: '',
        //         supported: false,
        //         messageUnsupported: 'Het sorteren van blokken wordt enkel ondersteunt op desktop',
        //     },
        //     {
        //         id: 'add-offset',
        //         name: 'Set Offsets in Breakpoints',
        //         supported: false,
        //         messageUnsupported: 'Toevoegen van offset is niet ondersteunt',
        //     },
        //     // {
        //     //     id: 'block-area-nesting',
        //     //     name: 'Nested areas',
        //     //     supported: false,
        //     //     messageUnsupported: 'Geneste area\'s worden niet ondersteund',
        //     // },
        // ]

        if (! this.features) {
            this.fetchBlueprintData();
        }

        return this.features;
    }

    /**
     * Get the posible breakpoints
     * @returns {[{name: string, description: string, id: string},{name: string, description: string, id: string},{name: string, description: string, id: string},{name: string, description: string, id: string},{name: string, description: string, id: string},null,null]}
     */
    fetchSupportedBreakpoints() {
        if( ! this.supportedBreakpoints) {
            this.fetchInterfaceData();
        }

        return this.supportedBreakpoints;
    }

    /**
     * Get the possible conditions for this breakpoint or this block in breakpoint
     * @param breakpoint
     * @param block
     * @returns {[{actionForm: {bundleId: string, definitionId: string}, name: string, description: string},{actionForm: {bundleId: string, definitionId: string}, name: string, description: string},{actionForm: {bundleId: string, definitionId: string}, name: string, description: string}]}
     */
    fetchConditions(breakpoint, block) {
        return [
            {
                name : "Is Authenticated as CMS user?",
                description : "description here",
                actionForm: {
                    bundleId: "action",
                    definitionId: "form",
                }
            },
            {
                name : "User has press role",
                description : "...",
                actionForm: {
                    bundleId: "action",
                    definitionId: "form",
                }
            },
            {
                name : "Device is mobile",
                description : "...",
                actionForm: {
                    bundleId: "action",
                    definitionId: "form",
                }
            }
        ]
    }

    /**
     * Get blueprint url
     * @return {*}
     */
    getPreviewUrl() {
        return this.previewUrl;
    }

    /**
     * Get placeholderUrl
     * @return {*}
     */
    getPlaceholderUrl() {
        return this.placeholderUrl;
    }

    getWidgetBaseUrl() {
        const url = new URL(this.vue.$getSetting('paths.baseBlueprint'));
        Object.keys(this.queryParams).forEach(key => {
            url.searchParams.append(key, this.queryParams[key]);
        });

        url.searchParams.append('blueprintId', this.id);

        return url.toString();
    }

    /**
     * Get all the needed data from the backend for a blueprint
     * @param id
     */
    async fetchBlueprintData() {
        const params = { ...this.queryParams, blueprintId: this.id};
        const url = this.vue.$getSetting('paths.baseBlueprint') + '/blueprint';

        let data;
        try {
            data = await this.vue.$get(url, params);
        } catch (error) {
            console.log(error);
        }

        if (! data) {
            return;
        }

        this.title = data.title;
        this.blocks = data.blocks;
        this.visibility = {
            published: data.published,
            publishedStart: data.publishedStart,
            publishedEnd: data.publishedEnd
        };
        this.features = data.features;
        this.grid = data.grid;
        this.vertical = data.vertical;
        this.breakpoints = data.breakpoints;
        this.metadataHandler = (new MetadataHandler(data.metadata));
        this.headerHandler = (new HeaderHandler(data.headers));
    }

    /**
     * Get all the needed data from the backend for the interface
     * @param id
     */
    async fetchInterfaceData() {
        const params = { ...this.queryParams, blueprintId: this.id};
        const url = this.vue.$getSetting('paths.baseBlueprint') + '/interface';

        const data = await this.vue.$get(url, params);

        if (!data) {
            return;
        }

        this.blockGroupings = data.blockGroupings;
        this.supportedBlocks = data.supportedBlocks;
        this.supportedBreakpoints = data.supportedBreakpoints;
        this.listUrl = data.listUrl;
        this.listLabel = data.listLabel;
        this.topBarWidgets = data.topBarWidgets;
        this.previewUrl = data.previewUrl;
        this.placeholderUrl = data.placeholderUrl;
        this.placeholderHandler = data.placeholderUrl
            ? new PlaceholderHandler(data.placeholderUrl)
            : null;
        this.versionsActive = data.versionsActive;
        this.versionsCount = data.versionsCount;
    }

    /**
     * Get the full preview for the current breakpoint
     * @param breakpoint
     */
    async getPreview(breakpoint) {
        const response  = await this.vue.$get('/../preview-html.html')
        return response.html;
    }

    /**
     * Save the current blueprint
     * @param breakpoints
     * @param blocks
     * @returns {boolean}
     */
    async save( breakpoints, blocks, visibility) {
        const params = { ...this.queryParams, blueprintId: this.id};
        const url = this.vue.$getSetting('paths.baseBlueprint') + '/blueprint/post';
        const body = {
            title: this.title,
            metadata: this.metadataHandler.getMetadata(),
            headers: this.headerHandler.getHeaders(),
            breakpoints: breakpoints,
            blocks: blocks,
            published: visibility.published
        };

        const data = await this.vue.$post(url, body, params);

        this.title = data.blueprint.title;
        this.blocks = data.blueprint.blocks;
        this.grid = data.blueprint.grid;
        this.breakpoints = data.blueprint.breakpoints;
        this.visibility = {
            published: data.blueprint.published,
            publishedStart: data.blueprint.publishedStart,
            publishedEnd: data.blueprint.publishedEnd
        };

        this.metadataHandler.setMetadata(data.blueprint.metadata);
        this.headerHandler.setHeaders(data.blueprint.headers);

        return data;
    }

    /**
     * Get the feature handler
     * @param callbackMethod
     * @returns {FeatureHandler}
     */
    getFeatureHandler(callbackMethod) {
        if (! this.featureHandler) {
            this.featureHandler = (new FeatureHandler(this.getFeatures(), callbackMethod));
        }

        return this.featureHandler;
    }

    /**
     * Get the metadata handler for a specific blueprint
     * @returns {Promise<*>}
     */
     async getMetadataHandler() {
        if (! this.metadataHandler) {
            await this.fetchBlueprintData();
        }

        return this.metadataHandler;
    }

    /**
     * Get the skinSettings handler for a specific blueprint
     * @returns {Promise<*>}
     */
    getSkinSettingsHandler() {
        if (! this.skinSettingsHandler) {
            this.skinSettingsHandler = (new SkinSettingsHandler())
        }

        return this.skinSettingsHandler;
    }

    /**
     * Get the header handler for a specific blueprint
     * @returns {Promise<*>}
     */
     async getHeaderHandler() {
        if (! this.headerHandler) {
            await this.fetchBlueprintData();
        }

        return this.headerHandler;
    }

    /**
     * Get the skinSettings handler for a specific blueprint
     * @returns {Promise<*>}
     */
    getPreviewHandler() {
        if (! this.previewHandler) {
            this.previewHandler = (new PreviewHandler(this.getPreviewUrl()));
        }

        return this.previewHandler;
    }

    /**
     * Get the placeholderHandler
     * @return {placeholderHandler}
     */
    getPlaceholderHandler() {
        if (! this.placeholderHandler && this.getPlaceholderUrl()) {
            this.placeholderHandler = new PlaceholderHandler(this.getPlaceholderUrl());
        }

        return this.placeholderHandler;
    }

    /**
     * We construct the url to get the form for the settings of a block
     * @param blockType
     * @param verticalId
     * @param settings
     * @param hashId
     * @returns {string}
     */
    getSettingsFormUrl(blockType, verticalId, blueprintId, hashId, versionId) {
        const url = new URL(this.vue.$getSetting('paths.baseBlueprint') + '/settings/block/' + blockType + (hashId ? '/' + hashId : '/new'));
        url.searchParams.append('verticalId', verticalId);
        url.searchParams.append('blueprintId', blueprintId);

        if (versionId) {
            url.searchParams.append('versionId', versionId);
        }

        return url.toString();
    }

    /**
     * We construct the url to save the form for the settings of a block
     * @param blockType
     * @param verticalId
     * @param hashId
     * @returns {string}
     */
    getSaveSettingsFormUrl(blockType, verticalId, blueprintId, hashId, versionId) {
        const url = new URL(this.vue.$getSetting('paths.baseBlueprint') + '/settings/block/' + blockType + (hashId ? '/' + hashId : '/new') + '/post') ;
        url.searchParams.append('verticalId', verticalId);
        url.searchParams.append('blueprintId', blueprintId);

        if (versionId) {
            url.searchParams.append('versionId', versionId);
        }

        return url.toString();
    }

    /**
     * Return the defined event listeners
     * @returns {{saveButtonClicked: dataProvider.saveButtonClicked, propertyChanged: dataProvider.propertyChanged, save: (function(*, *): boolean), activeBreakpointsChanged: dataProvider.activeBreakpointsChanged, orderChanged: dataProvider.orderChanged}}
     */
    getEventListeners() {
        return {
            saveButtonClicked: this.saveButtonClicked,
            propertyChanged: this.propertyChanged,
            orderChanged: this.orderChanged,
            activeBreakpointsChanged: this.activeBreakpointsChanged,
            save: this.save.bind(this),
        }
    }

    saveButtonClicked( breakpoints, blocks) {
        //console.log('saveButtonClicked');
    }

    propertyChanged( breakpoints, blocks) {
        //console.log('propertyChanged');
    }

    orderChanged( breakpoints, blocks) {
        //console.log('orderChanged');
    }

    activeBreakpointsChanged(newBreakpointIds) {
        //console.log('activeBreakpointsChanged', newBreakpointIds);
    }
}
